// AudienceBadge.tsx — small badge shown on a post card for any non-'everyone'
// audience, plus the "Scheduled" label for the author's own unpublished drafts.
//
// The payload already enforces the privacy boundary (regions/people arrays are
// present only on the author's own posts) — this renders exactly what's there,
// it never infers more than the server sent.

import styled from 'styled-components';
import type { PostAudience } from '@/core/models/post';
import { Icon } from '@/icons';
import type { IconName } from '@/icons';

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const AUDIENCE_ICON: Record<PostAudience['type'], IconName> = {
  everyone: 'globe',
  followers: 'users',
  regions: 'map-pin',
  people: 'user-check',
  public: 'globe',
  unlisted: 'link',
};

const AUDIENCE_LABEL: Record<PostAudience['type'], string> = {
  everyone: 'Everyone',
  followers: 'Followers',
  regions: 'Regions',
  people: 'Specific people',
  public: 'Public',
  unlisted: 'Unlisted',
};

export interface AudienceBadgeProps {
  audience: PostAudience;
}

/** Renders nothing for the default 'everyone' audience — the badge is only for the exceptions. */
export function AudienceBadge({ audience }: AudienceBadgeProps) {
  if (audience.type === 'everyone') return null;
  const detail =
    audience.type === 'regions' && audience.regions?.length
      ? ` (${audience.regions.join(', ')})`
      : audience.type === 'people' && audience.people?.length
        ? ` (${audience.people.length})`
        : '';
  return (
    <Badge data-testid="audience-badge" title={`${AUDIENCE_LABEL[audience.type]}${detail}`}>
      <Icon name={AUDIENCE_ICON[audience.type]} size={13} />
      {AUDIENCE_LABEL[audience.type]}
      {detail}
    </Badge>
  );
}

const SchedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 500;
  color: ${({ theme }) => theme.color.textMuted};
`;

export interface ScheduledLabelProps {
  scheduledAt: string;
}

export function ScheduledLabel({ scheduledAt }: ScheduledLabelProps) {
  const when = new Date(scheduledAt).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  return (
    <SchedBadge data-testid="scheduled-label">
      <Icon name="calendar-clock" size={13} />
      Scheduled for {when}
    </SchedBadge>
  );
}

/** 1.23.00 Phase 5: a draft — unpublished with no scheduledAt. Mirrors
 * ScheduledLabel's styling; only ever shown to the author (drafts are
 * author-only visible, same as scheduled posts). */
export function DraftLabel() {
  return (
    <SchedBadge data-testid="draft-label">
      <Icon name="edit" size={13} />
      Draft
    </SchedBadge>
  );
}
